import { useState, useEffect, useCallback } from 'react';
import { Plug, ShieldCheck, Save, CheckCircle2 } from 'lucide-react';

import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { Spinner }       from '@/components/ui/Spinner';
import { cn }            from '@/utils/cn';
import { integrationSettingsApi, type IntegrationSettingDto } from '@/api/integrationMapping.api';

export const IntegrationSettingsPage = () => {
  const { hasPermission } = usePermission();
  const canManage = hasPermission(Permissions.INTEGRATION_SETTINGS_MANAGE);

  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [mode,    setMode]    = useState<'Standalone' | 'Integrated'>('Standalone');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s: IntegrationSettingDto = await integrationSettingsApi.get();
      setEnabled(s.integrationEnabled);
      setMode(s.integrationMode);
    } catch (err) {
      console.error('Failed to load integration settings', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await integrationSettingsApi.update(enabled, mode);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.error('Failed to save integration settings', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><Spinner size="lg" /></div>;
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <Plug size={20} className="text-signal" />
          Integration Settings
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Control how this company integrates with myPOS ONE.
        </p>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-2xl space-y-5">
          {/* Enable toggle */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 shadow-card">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-semibold text-slate-900 dark:text-white">Enable integration</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  When enabled, postings from myPOS ONE are accepted and master-ownership rules apply.
                </p>
              </div>
              <button
                role="switch" aria-checked={enabled} disabled={!canManage}
                onClick={() => setEnabled(v => !v)}
                className={cn('relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors',
                  enabled ? 'bg-signal' : 'bg-slate-300 dark:bg-slate-600',
                  !canManage && 'opacity-50 cursor-not-allowed')}
              >
                <span className={cn('inline-block h-5 w-5 transform rounded-full bg-white dark:bg-slate-800 shadow transition-transform mt-0.5',
                  enabled ? 'translate-x-5' : 'translate-x-0.5')} />
              </button>
            </div>
          </div>

          {/* Mode */}
          <div className={cn('bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 shadow-card transition-opacity',
            !enabled && 'opacity-50 pointer-events-none')}>
            <h2 className="font-semibold text-slate-900 dark:text-white mb-3">Integration mode</h2>
            <div className="space-y-2">
              {([
                { v: 'Standalone', t: 'Standalone', d: 'Each system maintains its own masters independently. No ownership enforcement.' },
                { v: 'Integrated', t: 'Integrated', d: 'Master-ownership rules are enforced: myLedger owns financial masters; myPOS owns operational masters and maps them here before posting.' },
              ] as const).map(opt => (
                <button key={opt.v} disabled={!canManage} onClick={() => setMode(opt.v)}
                  className={cn('w-full flex items-start gap-3 p-3 rounded-lg border text-left transition-colors',
                    mode === opt.v
                      ? 'border-signal bg-signal/5'
                      : 'border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700/40')}>
                  <span className={cn('mt-0.5 flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center',
                    mode === opt.v ? 'border-signal' : 'border-slate-300 dark:border-slate-500')}>
                    {mode === opt.v && <span className="w-2 h-2 rounded-full bg-signal" />}
                  </span>
                  <span>
                    <span className="flex items-center gap-1.5 font-medium text-slate-900 dark:text-white">
                      {opt.v === 'Integrated' && <ShieldCheck size={14} className="text-signal" />}{opt.t}
                    </span>
                    <span className="block text-xs text-slate-500 dark:text-slate-400 mt-0.5">{opt.d}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Save */}
          {canManage && (
            <div className="flex items-center gap-3">
              <button onClick={save} disabled={saving}
                className="px-4 py-2 text-sm font-semibold text-white bg-signal hover:bg-signal-light rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5">
                {saving ? <><Spinner size="sm" /> Saving…</> : <><Save size={14} /> Save settings</>}
              </button>
              {saved && (
                <span className="text-sm text-teal-600 dark:text-teal-400 flex items-center gap-1">
                  <CheckCircle2 size={15} /> Saved
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
